import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SubmitButton from '../../../components/SubmitButton';
import {
  FilterBar,
  ExportCsvButton,
  AdminNotice,
  TabBar,
  EmptyState,
  SectionHead,
} from '../../../components/admin/Shared';
import { axiosKK } from '../../../services/networkRequest';
import {
  BulkReorderRouteRequest,
  LogisticsDriverOption,
  LogisticsDriversResponse,
  RecomputeRouteRequest,
  RoutePhase,
  RouteStopDTO,
  RouteStopsResponse,
} from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import UserContext from '../../../contexts/UserContext';
import { Role } from '../../../utilities/Role';
import RoutePlanList, { RoutePlanGroup } from './RoutePlanList';
import RouteMap from '../../../components/admin/Logistics/RouteMap';
import { DateDropdown } from '../../../components/DateDropdown';
import Checkbox from '../../../components/Checkbox';

type RoutePlanView = 'map' | 'list';

/** What `POST …/recompute` answers with: one summary per route it rebuilt. */
interface RouteRecomputeResponse {
  results?: { driverId: number | null; phase: string; stopsCount: number; usedGoogle: boolean }[];
}

export function recomputeBanner(results: NonNullable<RouteRecomputeResponse['results']>): string {
  const stopsCount = results.reduce((n, r) => n + (r.stopsCount || 0), 0);
  if (stopsCount === 0) {
    return 'Recomputed: no stops for this partner on this day. Only orders assigned to the partner with a collection or return that day are planned.';
  }
  const optimised = results.some((r) => r.usedGoogle);
  return `Recomputed ${stopsCount} stop${stopsCount === 1 ? '' : 's'}${
    optimised ? ', optimised with Google Maps.' : ' in a basic order (Google route optimisation is not set up).'
  }`;
}

function formatLockTimestamp(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface RoutePlanTabProps {
  partnerId: number | null;
}

export default function RoutePlanTab({ partnerId }: RoutePlanTabProps) {
  const history = useHistory();
  const location = useLocation();

  // --- Sub-tab state via URL query (default: map) ---------------------
  const initialView: RoutePlanView = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('view') === 'list' ? 'list' : 'map';
  }, [location.search]);
  const [view, setView] = useState<RoutePlanView>(initialView);

  const switchView = (next: RoutePlanView) => {
    setView(next);
    const params = new URLSearchParams(location.search);
    if (next === 'map') {
      params.delete('view');
    } else {
      params.set('view', next);
    }
    const search = params.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  };

  const [date, setDate] = useState<string>('');
  const [driverId, setDriverId] = useState<string>('');
  const [phase, setPhase] = useState<RoutePhase | ''>('');
  const [includeUnassigned, setIncludeUnassigned] = useState(true);

  const [drivers, setDrivers] = useState<LogisticsDriverOption[]>([]);
  const [stops, setStops] = useState<RouteStopDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [netError, setNetError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<string>();
  // Bumped after a recompute to load the stops again. The recompute answers with a
  // summary per route (`results`), not the stops, and reading `stops` off it emptied
  // the list every time Recompute was pressed.
  const [reloadKey, setReloadKey] = useState(0);
  const keepBannerOnReload = useRef(false);
  const [lockedAt, setLockedAt] = useState<string | undefined>(undefined);
  const [lockedBy, setLockedBy] = useState<number | undefined>(undefined);
  const [lockedByName, setLockedByName] = useState<string | undefined>(undefined);

  const { user } = useContext(UserContext);

  const userRole = user?.role;
  const isAdminOrMaster =
    userRole === Role.Admin ||
    userRole === Role.Warehouse ||
    userRole === 'PARTNER_MASTER' ||
    userRole === 'MASTER';
  const canControlLogistics = Boolean(
    isAdminOrMaster ||
      (user as unknown as { canControlLogistics?: boolean } | undefined)
        ?.canControlLogistics,
  );

  const isLocked = Boolean(lockedAt);

  useEffect(() => {
    if (partnerId == null) {
      setDrivers([]);
      return;
    }
    axiosKK
      .get<LogisticsDriversResponse>('/logistics/dashboard/partner/drivers', {
        params: { partnerId },
      })
      .then(({ data }) => setDrivers(data.drivers || []))
      .catch((err) => errorMessage(setNetError)(err));
  }, [partnerId]);

  useEffect(() => {
    if (partnerId == null || !date) {
      setStops([]);
      setLockedAt(undefined);
      setLockedBy(undefined);
      setLockedByName(undefined);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);
    if (keepBannerOnReload.current) keepBannerOnReload.current = false;
    else setBanner(undefined);

    const params: any = { includeUnassigned, partnerId };
    if (driverId) params.driverId = Number(driverId);
    if (phase) params.phase = phase;

    axiosKK
      .get<RouteStopsResponse>(`/logistics/dashboard/partner/route-plan/${date}/stops`, { params })
      .then(({ data }) => {
        if (cancelled) return;
        const sorted = (data.stops || []).slice().sort((a, b) => a.routePosition - b.routePosition);
        setStops(sorted);
        setLockedAt(data.lockedAt);
        setLockedBy(data.lockedBy);
        setLockedByName(data.lockedByName);
      })
      .catch((err) => {
        if (!cancelled) errorMessage(setNetError)(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [date, driverId, phase, includeUnassigned, partnerId, reloadKey]);

  const groups: RoutePlanGroup[] = useMemo(() => {
    const map = new Map<string, RouteStopDTO[]>();
    stops.forEach((s) => {
      const key = `${s.driverId ?? 'unassigned'}__${s.phase}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    });
    return Array.from(map.entries()).map(([key, items]) => ({
      key,
      driverId: items[0].driverId,
      phase: items[0].phase,
      items: items.slice().sort((a, b) => a.routePosition - b.routePosition),
    }));
  }, [stops]);

  const driverName = (id: number | null): string => {
    if (id == null) return 'Unassigned';
    const d = drivers.find((x) => x.id === id);
    return d ? d.name : `Driver #${id}`;
  };

  const onReorder = (
    groupDriverId: number | null,
    groupPhase: RoutePhase,
    reorderedGroupItems: RouteStopDTO[],
  ) => {
    if (groupDriverId == null) {
      setNetError('Cannot reorder unassigned stops — assign a driver first.');
      return;
    }

    const newStops = stops.map((s) => {
      if (s.driverId !== groupDriverId || s.phase !== groupPhase) return s;
      const newPos = reorderedGroupItems.findIndex((g) => g.id === s.id);
      return { ...s, routePosition: newPos };
    });
    const previousStops = stops;
    setStops(newStops);

    const payload: BulkReorderRouteRequest = {
      driverId: groupDriverId,
      phase: groupPhase,
      stopIds: reorderedGroupItems.map((s) => s.id),
    };
    setBusy(true);
    setNetError(undefined);
    axiosKK
      .put(`/logistics/dashboard/partner/route-plan/${date}/order`, payload, {
        params: partnerId != null ? { partnerId } : undefined,
      })
      .then(() => setBanner('Route reorder saved.'))
      .catch((err) => {
        errorMessage(setNetError)(err);
        setStops(previousStops);
      })
      .finally(() => setBusy(false));
  };

  const recompute = (groupDriverId?: number, groupPhase?: RoutePhase) => {
    setBusy(true);
    setBanner(undefined);
    setNetError(undefined);
    const payload: RecomputeRouteRequest = {};
    if (groupDriverId != null) payload.driverId = groupDriverId;
    if (groupPhase) payload.phase = groupPhase;

    axiosKK
      .post<RouteRecomputeResponse>(
        `/logistics/dashboard/partner/route-plan/${date}/recompute`,
        payload,
        { params: partnerId != null ? { partnerId } : undefined }
      )
      .then(({ data }) => {
        setBanner(recomputeBanner(data.results || []));
        keepBannerOnReload.current = true;
        setReloadKey((k) => k + 1);
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setBusy(false));
  };

  const lockDay = () => {
    if (!canControlLogistics || isLocked) return;
    const previousLockedAt = lockedAt;
    const previousLockedBy = lockedBy;
    const previousLockedByName = lockedByName;
    const optimisticTimestamp = new Date().toISOString();
    setLockedAt(optimisticTimestamp);
    setLockedBy(user?.id);
    setLockedByName(
      user?.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : undefined,
    );
    setBusy(true);
    setBanner(undefined);
    setNetError(undefined);
    axiosKK
      .post<{ lockedAt?: string; lockedBy?: number; lockedByName?: string }>(
        `/logistics/dashboard/partner/route-plan/${date}/lock`,
        undefined,
        { params: partnerId != null ? { partnerId } : undefined }
      )
      .then(({ data }) => {
        if (data && (data.lockedAt || data.lockedBy || data.lockedByName)) {
          setLockedAt(data.lockedAt ?? optimisticTimestamp);
          setLockedBy(data.lockedBy ?? user?.id);
          setLockedByName(data.lockedByName);
        }
        setBanner(`Locked ${date}.`);
      })
      .catch((err) => {
        setLockedAt(previousLockedAt);
        setLockedBy(previousLockedBy);
        setLockedByName(previousLockedByName);
        errorMessage(setNetError)(err);
      })
      .finally(() => setBusy(false));
  };

  const unlockDay = () => {
    if (!isAdminOrMaster || !isLocked) return;
    const previousLockedAt = lockedAt;
    const previousLockedBy = lockedBy;
    const previousLockedByName = lockedByName;
    setLockedAt(undefined);
    setLockedBy(undefined);
    setLockedByName(undefined);
    setBusy(true);
    setBanner(undefined);
    setNetError(undefined);
    axiosKK
      .post(
        `/logistics/dashboard/partner/route-plan/${date}/unlock`,
        undefined,
        { params: partnerId != null ? { partnerId } : undefined }
      )
      .then(() => setBanner(`Unlocked ${date}.`))
      .catch((err) => {
        setLockedAt(previousLockedAt);
        setLockedBy(previousLockedBy);
        setLockedByName(previousLockedByName);
        errorMessage(setNetError)(err);
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <SectionHead
        title="Route plan"
        caption="Daily route view per driver. Stops auto-sequence from the partner's service area and time windows; drag-to-reorder to override."
        infoKey="logistics.route-plan"
      />
      <AdminNotice intent="error">{netError}</AdminNotice>
      <AdminNotice intent="success">{banner}</AdminNotice>

      <section className="admin-route-plan">
        {isLocked && (
          <p className="route-plan-lock-status" role="status">
            Day is locked.
            {lockedByName ? ` Locked by ${lockedByName}` : ''}
            {lockedAt ? ` at ${formatLockTimestamp(lockedAt)}` : ''}
            .
          </p>
        )}

        <TabBar<RoutePlanView>
          variant="sub"
          items={[
            { id: 'map', label: 'Map' },
            { id: 'list', label: 'List' },
          ]}
          activeId={view}
          onChange={switchView}
          ariaLabel="Route plan view"
        />

        <FilterBar
          actions={
            <>
              <SubmitButton onClick={() => recompute()} disabled={busy || isLocked}>
                Recompute
              </SubmitButton>
              {isLocked && isAdminOrMaster ? (
                <SubmitButton
                  onClick={unlockDay}
                  disabled={busy}
                  aria-label="Unlock day"
                >
                  Unlock day
                </SubmitButton>
              ) : (
                <SubmitButton
                  onClick={lockDay}
                  disabled={busy || isLocked || !canControlLogistics}
                  red
                  title={
                    isLocked
                      ? `Day is locked.${lockedByName ? ` Locked by ${lockedByName}` : ''}${lockedAt ? ` at ${formatLockTimestamp(lockedAt)}` : ''}.`
                      : !canControlLogistics
                        ? 'You do not have permission to lock days for this partner.'
                        : undefined
                  }
                  aria-label="Lock day"
                >
                  Lock day
                </SubmitButton>
              )}
              <ExportCsvButton
                endpoint="/logistics/dashboard/partner/route-plan"
                filename={`route-plan-${date}.csv`}
                params={{
                  date,
                  driverId: driverId ? Number(driverId) : undefined,
                  phase: phase || undefined,
                  partnerId: partnerId ?? undefined,
                }}
                disabled={partnerId == null}
              />
            </>
          }
        >
          <FilterBar.Item label="Date" htmlFor="route-plan-date">
            <DateDropdown
              id="route-plan-date"
              value={date || null}
              onChange={(iso) => setDate(iso ?? '')}
              label="Route plan date"
              placeholder="Select date"
            />
          </FilterBar.Item>
          <FilterBar.Item label="Driver" htmlFor="route-plan-driver">
            <select
              id="route-plan-driver"
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
            >
              <option value="">All drivers</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </FilterBar.Item>
          <FilterBar.Item label="Phase" htmlFor="route-plan-phase">
            <select
              id="route-plan-phase"
              value={phase}
              onChange={(e) => setPhase(e.target.value as RoutePhase | '')}
            >
              <option value="">All phases</option>
              <option value="COLLECTION">Collection</option>
              <option value="DELIVERY">Delivery</option>
            </select>
          </FilterBar.Item>
          <FilterBar.Item>
            <Checkbox
              checked={includeUnassigned}
              onChange={(e) => setIncludeUnassigned(e.target.checked)}
              label="Include unassigned"
            />
          </FilterBar.Item>
        </FilterBar>

        {partnerId == null ? (
          <EmptyState
            variant="select-prerequisite"
            title="Select a partner"
            body="Pick a partner from the picker above to load the route plan."
          />
        ) : !date ? (
          <EmptyState
            variant="select-prerequisite"
            title="Select a date"
            body="Choose a date in the filter above to load the route plan."
          />
        ) : view === 'map' ? (
          <RouteMap
            stops={stops}
            date={date}
            partnerId={partnerId}
            canControlLogistics={canControlLogistics}
            isLocked={isLocked}
            onOptimise={() => recompute()}
          />
        ) : (
          <RoutePlanList
            partnerId={partnerId}
            loading={loading}
            groups={groups}
            busy={busy}
            driverName={driverName}
            onReorder={onReorder}
            onRecomputeGroup={(gd, gp) => recompute(gd, gp)}
          />
        )}
      </section>
    </>
  );
}
